import { Link } from 'react-router-dom';
import { FiPlay, FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Button } from "@/components/ui/button";
import { useDemoMode } from '@/context/DemoModeContext';

const NewHeroSection = () => {
  const { isConnected } = useAccount();

  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-900 via-dark-900 to-dark-800 z-0"></div>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-500/10 rounded-full filter blur-3xl"
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 2
          }}
          className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-secondary-500/10 rounded-full filter blur-3xl"
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1
          }}
          className="absolute top-1/2 right-1/3 w-48 h-48 bg-accent-500/10 rounded-full filter blur-3xl"
        />
      </div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('https://raw.githubusercontent.com/shadcn-ui/ui/main/apps/www/public/grid.svg')] bg-center opacity-10 z-0"></div>
      
      <div className="container-custom relative z-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Hero content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-block mb-4 px-4 py-1.5 rounded-full bg-dark-700 border border-dark-600"
            >
              <span className="text-gradient font-medium">Powered by Livepeer & IPFS</span>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="heading-gradient">Decentralized</span> Media Streaming & NFT Platform
            </h1>
            
            <p className="text-gray-300 text-lg md:text-xl mb-8 max-w-xl">
              Connect with creators, unlock exclusive content, and own digital assets in a tokenized creative ecosystem.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/explore">
                <Button variant="default" className="shadow-3d group">
                  <FiPlay className="mr-2" />
                  Launch App
                  <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/explore">
                <Button variant="outline" className="shadow-3d group">
                  Explore Platform
                  <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            
            <div className="mt-12 flex items-center space-x-6">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 border-2 border-dark-900 shadow-md flex items-center justify-center text-xs font-bold text-white">
                    {['JD', 'AK', 'MR', 'TS'][i-1]}
                  </div>
                ))}
              </div>
              <p className="text-gray-400">
                <span className="text-white font-semibold">1,000+</span> creators already on the platform
              </p>
            </div>
          </motion.div>
          
          {/* Hero image/video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-dark-700 shadow-glow aspect-video">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 z-10"></div>
              <img 
                src="https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80" 
                alt="FTTF.World Platform" 
                className="w-full h-full object-cover"
              />
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <motion.div 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-16 h-16 rounded-full bg-primary-600/80 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors shadow-glow"
                >
                  <FiPlay className="text-white ml-1" size={24} />
                </motion.div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute top-4 left-4 badge-primary z-20">Live Streaming</div>
              <div className="absolute top-4 right-4 badge-secondary z-20">NFT Marketplace</div>
            </div>
            
            {/* Floating cards */}
            <motion.div 
              className="absolute -bottom-6 -right-6 glass-card p-4 z-30 max-w-xs shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-accent-500 to-secondary-500 shadow-accent-glow flex items-center justify-center text-white font-bold">
                  VIP
                </div>
                <div>
                  <p className="text-white font-medium">Exclusive Drop</p>
                  <p className="text-xs text-gray-400">Token-gated content</p>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-300">Unlock with FTTF</span>
                <span className="badge-accent">100 FTTF</span>
              </div>
            </motion.div>
            
            <motion.div 
              className="absolute -top-6 -left-6 glass-card p-3 z-30 max-w-[180px] shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary-500 to-primary-700 shadow-glow flex items-center justify-center text-white text-xs font-bold">
                  LIVE
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Live Now</p>
                  <p className="text-xs text-gray-400">1.2k watching</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <p className="text-gray-400 text-sm mb-2">Scroll to explore</p>
          <motion.div 
            className="w-6 h-10 rounded-full border-2 border-gray-500 flex items-center justify-center"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <motion.div 
              className="w-1.5 h-1.5 rounded-full bg-primary-500"
              animate={{ y: [0, 4, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewHeroSection;